import React, { useEffect, useState, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { api, User } from '../lib/api';
import { TierBadge, TierType } from './TierBadge';
import bs58 from 'bs58';

interface WalletButtonProps {
  onAuthSuccess?: (user: User) => void;
  onLogout?: () => void;
}

export const WalletButton: React.FC<WalletButtonProps> = ({ onAuthSuccess, onLogout }) => {
  const { publicKey, signMessage, disconnect, connected } = useWallet();
  const { setVisible } = useWalletModal();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [tier, setTier] = useState<TierType>('free');
  
  // Use a ref to avoid infinite sign challenge loops
  const signingWalletRef = useRef<string | null>(null);

  // Load user from API if JWT is present
  const loadMe = async () => {
    try {
      setLoading(true);
      const res = await api.me();
      setUser(res.user);
      if (onAuthSuccess) onAuthSuccess(res.user);
      
      // Try to determine tier from local wallet balance
      await resolveTier(res.user.wallet);
    } catch (err) {
      console.error('Failed to load user session, logging out', err);
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const resolveTier = async (walletAddress: string) => {
    try {
      // In MVP, we can simulate or fetch from the feed response viewer_tier,
      // or we can run a simple check. Let's write a mock/placeholder resolver
      // that is updated when posts load, or queries token account balance.
      // We will read the tier thresholds from the feed and check user's tier.
      // Let's set it to 'free' by default and let ContentCards update it when they resolve
      // the viewer's tier from the API, or we can fetch a feed page to get viewer_tier.
      const feedRes = await api.getPosts(1, 1).catch(() => null);
      if (feedRes && feedRes.posts.length > 0) {
        // If there's a locked post, it returns required_tier + viewer_tier.
        // Let's find any post with viewer_tier
        const postWithTier = feedRes.posts.find(p => p.viewer_tier);
        if (postWithTier?.viewer_tier) {
          setTier(postWithTier.viewer_tier as TierType);
          return;
        }
      }
      
      // Fallback: Check if we have token balance in localStorage or default to free
      setTier('free');
    } catch {
      setTier('free');
    }
  };

  // Sign message authentication
  const handleAuth = async () => {
    if (!publicKey || !signMessage) return;
    const walletStr = publicKey.toBase58();
    
    // Prevent concurrent signing prompts for the same wallet
    if (signingWalletRef.current === walletStr) return;
    signingWalletRef.current = walletStr;

    try {
      setLoading(true);
      
      // 1. Get challenge nonce
      const { nonce } = await api.challenge();

      // 2. Sign nonce with wallet
      const messageBytes = new TextEncoder().encode(nonce);
      const signatureBytes = await signMessage(messageBytes);
      const signatureBase58 = bs58.encode(signatureBytes);

      // 3. Verify on backend
      const res = await api.verify(walletStr, signatureBase58, nonce);
      setUser(res.user);
      
      if (onAuthSuccess) onAuthSuccess(res.user);
      await resolveTier(walletStr);
    } catch (err) {
      console.error('Wallet signing auth failed', err);
      // Disconnect wallet if authentication failed
      disconnect();
    } finally {
      setLoading(false);
      signingWalletRef.current = null;
    }
  };

  const handleLogout = () => {
    api.logout();
    setUser(null);
    setTier('free');
    disconnect();
    if (onLogout) onLogout();
  };

  // Run on mount or JWT presence
  useEffect(() => {
    const jwt = localStorage.getItem('solemate_jwt');
    if (jwt && connected && publicKey) {
      loadMe();
    }
  }, [connected, publicKey]);

  // Monitor wallet connection state changes
  useEffect(() => {
    const jwt = localStorage.getItem('solemate_jwt');
    if (connected && publicKey) {
      if (!jwt || (user && user.wallet !== publicKey.toBase58())) {
        handleAuth();
      }
    } else if (!connected) {
      if (jwt || user) {
        handleLogout();
      }
    }
  }, [connected, publicKey]);

  const truncateWallet = (addr: string) => {
    return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
  };

  if (loading) {
    return (
      <button className="btn btn-secondary btn-sm shimmer" style={{ width: '140px', height: '38px' }} disabled>
        🦶 Syncing...
      </button>
    );
  }

  if (publicKey && user) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <TierBadge tier={tier} />
        <button className="btn btn-secondary btn-sm" onClick={handleLogout} title="Click to disconnect">
          {truncateWallet(user.wallet)} 🚪
        </button>
      </div>
    );
  }

  return (
    <button className="btn btn-outline-gradient btn-sm" onClick={() => setVisible(true)}>
      Connect Wallet 🦶
    </button>
  );
};
